/**
 * 로그인 화면
 * ---------------------------------------------------------------------------
 * 서버가 돌려주는 status 에 따라 단계가 갈린다.
 *   SUCCESS                  → 로그인 완료
 *   MFA_REQUIRED             → 2차 인증 코드 입력
 *   MFA_ENROLLMENT_REQUIRED  → 인증 앱 등록 필요
 *   PASSWORD_CHANGE_REQUIRED → 비밀번호 변경 필요
 *   TENANT_REQUIRED          → 소속 기업 선택 (같은 이메일이 여러 기업에 있는 경우)
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Checkbox, Form, Input, Select, Space, Typography } from 'antd';
import { LockOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons';
import { apiClient, setAccessToken, type NormalizedError } from '../../lib/api-client';
import { useAuth, type AuthProfile } from '../../lib/auth-store';

const { Title, Text } = Typography;

type Step = 'CREDENTIALS' | 'TENANT' | 'MFA';

interface LoginResponse {
  status: string;
  accessToken?: string;
  profile?: AuthProfile;
  evictedSessionCount?: number;
  challengeToken?: string;
  factors?: string[];
  hint?: string;
  tenants?: Array<{ slug: string; companyName: string }>;
}

/**
 * 로그인 후 제품 웹으로 돌려보낼 주소인지 확인한다.
 *
 * 허용 목록에 없는 주소는 돌려주지 않는다. 검사 없이 받아 주면 공격자가
 * `?next=https://...` 로 피싱 사이트에 보낼 수 있다(열린 리디렉션).
 * 목록은 환경 변수로 둔다 — 제품이 늘어날 때 코드를 고치지 않기 위해서다.
 */
function externalRedirect(next: string | null): string | null {
  if (!next) return null;

  const allowed = (import.meta.env.VITE_PRODUCT_ORIGINS ?? '')
    .split(',')
    .map((o: string) => o.trim())
    .filter(Boolean);

  try {
    const url = new URL(next, window.location.origin);
    if (url.origin === window.location.origin) return null; // 내부 경로다
    return allowed.includes(url.origin) ? url.toString() : null;
  } catch {
    return null;
  }
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setProfile } = useAuth();

  const [step, setStep] = useState<Step>('CREDENTIALS');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /*
   * 왜 로그인 화면으로 왔는지 알려 준다.
   *
   * 세션은 30분 동안 아무 것도 하지 않으면 끊긴다(기업 보안 정책). 그때
   * 아무 말 없이 로그인 화면만 뜨면 "왜 로그아웃됐지" 가 되고, 비밀번호가
   * 틀린 줄 알고 다시 입력해 보는 사람도 생긴다.
   */
  useEffect(() => {
    if (params.get('reason') === 'expired') {
      setNotice('자리를 비운 사이 보안 정책에 따라 세션이 종료되었습니다. 다시 로그인해 주세요.');
    }
  }, [params]);

  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [tenants, setTenants] = useState<Array<{ slug: string; companyName: string }>>([]);
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [mfaHint, setMfaHint] = useState<string>('');
  const [mfaFactors, setMfaFactors] = useState<string[]>([]);
  const [useBackupCode, setUseBackupCode] = useState(false);

  const finish = (data: LoginResponse) => {
    if (!data.accessToken || !data.profile) return;

    setAccessToken(data.accessToken);
    setProfile(data.profile);

    if (data.evictedSessionCount && data.evictedSessionCount > 0) {
      // 동시 접속 정책으로 다른 기기 세션이 끊겼음을 알려 준다.
      sessionStorage.setItem(
        'wnh:notice',
        `보안 정책에 따라 다른 기기의 로그인 ${data.evictedSessionCount}건이 종료되었습니다.`,
      );
    }

    const next = params.get('next') ?? params.get('redirect');
    const external = externalRedirect(next);

    // 제품 웹(WORKNECO 등)에서 넘어온 경우 그 화면으로 돌려보낸다.
    // react-router 의 navigate 는 외부 주소를 경로로 해석하므로 쓸 수 없다.
    if (external) {
      window.location.replace(external);
      return;
    }

    // 외부 주소가 아니면 내부 경로로만 이동한다. 검증을 통과하지 못한 값은
    // 버린다 — 여기서 그대로 쓰면 열린 리디렉션이 된다.
    navigate(next && next.startsWith('/') ? next : '/app', { replace: true });
  };

  const handleResponse = (data: LoginResponse) => {
    switch (data.status) {
      case 'SUCCESS':
        finish(data);
        break;

      case 'TENANT_REQUIRED':
        setTenants(data.tenants ?? []);
        setStep('TENANT');
        setNotice('이 이메일로 등록된 기업이 여러 곳입니다. 로그인할 기업을 선택해 주세요.');
        break;

      case 'MFA_REQUIRED':
        setChallengeToken(data.challengeToken ?? null);
        setMfaHint(data.hint ?? '');
        setMfaFactors(data.factors ?? []);
        setStep('MFA');
        break;

      case 'MFA_ENROLLMENT_REQUIRED':
        setError(
          '보안 정책에 따라 인증 앱 등록이 필요합니다. 관리자에게 등록 절차를 문의해 주세요.',
        );
        break;

      case 'PASSWORD_CHANGE_REQUIRED':
        setError('비밀번호 변경이 필요합니다. 관리자에게 문의해 주세요.');
        break;

      default:
        setError('처리할 수 없는 응답입니다.');
    }
  };

  const submitCredentials = async (values: {
    email: string;
    password: string;
    tenantSlug?: string;
  }) => {
    setLoading(true);
    setError(null);
    setNotice(null);

    try {
      setCredentials({ email: values.email, password: values.password });
      const { data } = await apiClient.post<LoginResponse>('/auth/login', values);
      handleResponse(data);
    } catch (err) {
      setError((err as NormalizedError).message);
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (values: { code: string; rememberDevice: boolean }) => {
    if (!challengeToken) return;

    setLoading(true);
    setError(null);

    try {
      const { data } = await apiClient.post<LoginResponse>('/auth/mfa/verify', {
        challengeToken,
        code: values.code,
        factorType: useBackupCode ? 'BACKUP_CODE' : mfaFactors[0],
        rememberDevice: values.rememberDevice,
      });
      handleResponse(data);
    } catch (err) {
      setError((err as NormalizedError).message);
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (!challengeToken) return;

    setLoading(true);
    try {
      const { data } = await apiClient.post<{ challengeToken: string }>('/auth/mfa/resend', {
        challengeToken,
      });
      setChallengeToken(data.challengeToken);
      setNotice('인증코드를 다시 보냈습니다.');
    } catch (err) {
      setError((err as NormalizedError).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 16 }}>
      <Card style={{ width: '100%', maxWidth: 420 }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={3} style={{ marginBottom: 4 }}>
              WORKnHub
            </Title>
            <Text type="secondary">통합 업무 플랫폼</Text>
          </div>

          {error && <Alert type="error" message={error} showIcon />}
          {notice && <Alert type="info" message={notice} showIcon />}

          {step === 'CREDENTIALS' && (
            <Form layout="vertical" onFinish={submitCredentials} requiredMark={false}>
              <Form.Item
                name="email"
                label="이메일"
                rules={[{ required: true, message: '이메일을 입력하세요' }]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="name@company.co.kr"
                  autoComplete="username"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="비밀번호"
                rules={[{ required: true, message: '비밀번호를 입력하세요' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  autoComplete="current-password"
                  size="large"
                />
              </Form.Item>

              <Button type="primary" htmlType="submit" loading={loading} block size="large">
                로그인
              </Button>
            </Form>
          )}

          {step === 'TENANT' && (
            <Form
              layout="vertical"
              onFinish={(values: { tenantSlug: string }) =>
                submitCredentials({ ...credentials, tenantSlug: values.tenantSlug })
              }
            >
              <Form.Item
                name="tenantSlug"
                label="소속 기업"
                rules={[{ required: true, message: '기업을 선택하세요' }]}
              >
                <Select
                  size="large"
                  placeholder="기업 선택"
                  options={tenants.map((t) => ({ value: t.slug, label: t.companyName }))}
                />
              </Form.Item>

              <Button type="primary" htmlType="submit" loading={loading} block size="large">
                계속
              </Button>
            </Form>
          )}

          {step === 'MFA' && (
            <Form
              layout="vertical"
              onFinish={submitMfa}
              initialValues={{ rememberDevice: false }}
            >
              <Alert
                type="info"
                showIcon
                icon={<SafetyOutlined />}
                message="2차 인증"
                description={useBackupCode ? '백업 코드를 입력하세요.' : mfaHint}
                style={{ marginBottom: 16 }}
              />

              <Form.Item
                name="code"
                label={useBackupCode ? '백업 코드' : '인증코드'}
                rules={[{ required: true, message: '코드를 입력하세요' }]}
              >
                <Input
                  size="large"
                  autoComplete="one-time-code"
                  inputMode={useBackupCode ? 'text' : 'numeric'}
                  maxLength={useBackupCode ? 10 : 6}
                  placeholder={useBackupCode ? 'XXXXXXXXXX' : '000000'}
                  style={{ letterSpacing: useBackupCode ? 2 : 8, textAlign: 'center' }}
                />
              </Form.Item>

              <Form.Item name="rememberDevice" valuePropName="checked">
                <Checkbox>이 기기를 신뢰함 (기업 정책에 따라 적용되지 않을 수 있습니다)</Checkbox>
              </Form.Item>

              <Space direction="vertical" style={{ width: '100%' }}>
                <Button type="primary" htmlType="submit" loading={loading} block size="large">
                  확인
                </Button>

                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  {!useBackupCode && mfaFactors.includes('EMAIL') && (
                    <Button type="link" onClick={resendCode} disabled={loading}>
                      코드 재발송
                    </Button>
                  )}
                  <Button type="link" onClick={() => setUseBackupCode(!useBackupCode)}>
                    {useBackupCode ? '인증코드로 입력' : '백업 코드 사용'}
                  </Button>
                </Space>
              </Space>
            </Form>
          )}
        </Space>
      </Card>
    </div>
  );
}
